import React, { useState, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import { Button } from '@/components/Button';
import { Avatar } from '@/components/Avatar';
import { Input } from '@/components/Input';
import { ticketService } from '@/services/ticketService';
import { ConversationType } from '@/types/api';
import type { Ticket, CreateConversationRequest } from '@/types/api';

interface ToolsPanelProps {
  ticket: Ticket;
  onTicketUpdate: (updates: Partial<Ticket>) => void;
  onConversationUpdate?: () => void; // Callback to refresh conversations
  canEdit: boolean;
  liveCollaborators: string[];
  className?: string;
}

/**
 * Tools Panel Component
 * Apps pane with collaboration tools and widgets
 */
export const ToolsPanel: React.FC<ToolsPanelProps> = ({
  ticket,
  onConversationUpdate,
  className
}) => {
  // Collaboration state
  const [activityFeed, setActivityFeed] = useState<Array<{
    id: string;
    actorName: string;
    action: string;
    timestamp: string;
    details?: string;
  }>>([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [quickNote, setQuickNote] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  // Clear error when user starts typing
  useEffect(() => {
    if (quickNote.trim() && noteError) {
      setNoteError(null);
    }
  }, [quickNote, noteError]);
  
  const loadActivity = useCallback(async () => {
    setIsLoadingActivity(true);
    setActivityError(null);
    try {
      const response = await ticketService.getTicketConversations(ticket.id);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to load recent activity.');
      }
      const activityTypes = new Set<number>([
        ConversationType.Activity,
        ConversationType.StatusChange,
        ConversationType.Assignment,
        ConversationType.PriorityChange,
        ConversationType.Escalation,
      ]);
      const feed = (response.data.conversations || [])
        .filter(conversation => activityTypes.has(Number(conversation.type ?? conversation.conversationType)))
        .map(conversation => ({
          id: conversation.id,
          actorName: conversation.author?.fullName || conversation.user?.fullName || 'System',
          action: conversation.content,
          timestamp: conversation.createdAt,
        }));
      setActivityFeed(feed);
    } catch (error) {
      setActivityFeed([]);
      setActivityError(error instanceof Error ? error.message : 'Failed to load recent activity.');
    } finally {
      setIsLoadingActivity(false);
    }
  }, [ticket.id]);

  useEffect(() => {
    void loadActivity();
  }, [loadActivity]);
  
  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };
  
  // Quick note handler
  const addQuickNote = useCallback(async () => {
    if (!quickNote.trim() || isAddingNote) return;
    
    setIsAddingNote(true);
    setNoteError(null);
    
    try {
      // Create internal conversation request
      const newNote: CreateConversationRequest = {
        ticketId: ticket.id,
        content: quickNote.trim(),
        isInternal: true, // Always internal for quick notes
        conversationType: ConversationType.Note,
      };
      
      // Call the API to create the internal note
      const response = await ticketService.createConversation(newNote);
      
      if (response.success && response.data) {
        // Clear the input field
        setQuickNote('');
        
        // Notify parent component to refresh conversations if callback provided
        onConversationUpdate?.();
        await loadActivity();
      } else {
        throw new Error(response.message || 'Failed to add note');
      }
    } catch (error) {
      console.error('❌ Failed to add quick note:', error);
      setNoteError(error instanceof Error ? error.message : 'Failed to add note. Please try again.');
    } finally {
      setIsAddingNote(false);
    }
  }, [quickNote, isAddingNote, ticket.id, onConversationUpdate, loadActivity]);
  
  return (
    <div className={clsx('flex flex-col h-full bg-white overflow-hidden', className)}>
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Activity Feed */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Recent Activity</h3>
          
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {isLoadingActivity ? (
              <p className="text-sm text-gray-500 text-center py-2">Loading activity…</p>
            ) : activityError ? (
              <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
                <p>{activityError}</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => void loadActivity()}>
                  Retry
                </Button>
              </div>
            ) : activityFeed.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-2">No system activity recorded.</p>
            ) : activityFeed.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-2 p-2">
                <Avatar
                  name={activity.actorName}
                  size="xs"
                  className="flex-shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{activity.actorName}</span>
                    {' '}
                    <span>{activity.action}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatRelativeTime(activity.timestamp)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Quick Notes */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Quick Notes</h3>
          
          <div className="space-y-2">
            <div className="flex space-x-2">
              <Input
                type="text"
                placeholder="Add a quick internal note..."
                value={quickNote}
                onChange={(e) => setQuickNote(e.target.value)}
                className={clsx(
                  "flex-1",
                  isAddingNote && "opacity-75"
                )}
                disabled={isAddingNote}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addQuickNote();
                  }
                }}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={addQuickNote}
                disabled={!quickNote.trim() || isAddingNote}
                className={clsx(
                  "px-3 min-w-[36px]",
                  isAddingNote && "opacity-75"
                )}
              >
                {isAddingNote ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle 
                      className="opacity-25" 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      stroke="currentColor" 
                      strokeWidth="4"
                    />
                    <path 
                      className="opacity-75" 
                      fill="currentColor" 
                      d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                )}
              </Button>
            </div>
            
            {/* Error message */}
            {noteError && (
              <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md border border-red-200">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{noteError}</span>
                </div>
              </div>
            )}
            
            {/* Help text */}
            <p className="text-xs text-gray-500">
              Quick notes are saved as internal comments and appear in the conversation history
            </p>
          </div>
        </div>
        
      </div>
    </div>
  );
};
